import { useState } from 'react';
import { Field } from '../../components/page';
import { ActionError, QueryView } from '../../components/states';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useApi } from '../../lib/auth';
import { useI18n } from '../../lib/i18n';
import { useApiQuery } from '../../lib/query';
import type { Me, TotpSetup } from '../../lib/types';

// Double authentification de l'utilisateur connecté (BA-004, obligatoire pour les admins en BA-045).

export function SecuritySection() {
  const t = useI18n().t;
  const api = useApi();
  const me = useApiQuery<Me>('/v1/me');
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<Error | null>(null);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      setCode('');
      await me.refetch();
    } catch (err) {
      setError(err as Error);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.settings.totpTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        <QueryView query={me}>
          {(user) => (
            <div className="flex flex-col gap-3">
              <p className="flex items-center gap-2 text-sm">
                {user.email}
                <Badge tone={user.totpEnabled ? 'success' : 'neutral'}>
                  {user.totpEnabled ? t.settings.totpEnabled : t.settings.totpDisabled}
                </Badge>
              </p>
              {!user.totpEnabled && !setup ? (
                <div>
                  <Button
                    onClick={() =>
                      void run(async () =>
                        setSetup(await api.request<TotpSetup>('POST', '/v1/auth/totp/setup')),
                      )
                    }
                  >
                    {t.settings.totpStart}
                  </Button>
                </div>
              ) : null}
              {!user.totpEnabled && setup ? (
                <>
                  <p className="text-sm">{t.settings.totpScan}</p>
                  <Field label={t.settings.totpSecret}>
                    <Input readOnly dir="ltr" value={setup.secret} className="font-mono" />
                  </Field>
                  <a href={setup.otpauthUri} className="text-sm text-primary underline">
                    otpauth://
                  </a>
                </>
              ) : null}
              {setup || user.totpEnabled ? (
                <div className="flex flex-wrap items-end gap-2">
                  <Field label={t.settings.totpCode}>
                    <Input
                      inputMode="numeric"
                      maxLength={6}
                      className="w-32"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      autoComplete="one-time-code"
                    />
                  </Field>
                  {user.totpEnabled ? (
                    <Button
                      variant="destructive"
                      disabled={code.length !== 6}
                      onClick={() =>
                        void run(() =>
                          api.request('POST', '/v1/auth/totp/disable', { body: { code } }),
                        )
                      }
                    >
                      {t.settings.totpDisable}
                    </Button>
                  ) : (
                    <Button
                      disabled={code.length !== 6}
                      onClick={() =>
                        void run(async () => {
                          await api.request('POST', '/v1/auth/totp/confirm', { body: { code } });
                          setSetup(null);
                          // Nouveau jeton sans restriction (TOTP obligatoire des admins, BA-045).
                          await api.refresh();
                        })
                      }
                    >
                      {t.settings.totpConfirm}
                    </Button>
                  )}
                </div>
              ) : null}
              <ActionError error={error} />
            </div>
          )}
        </QueryView>
      </CardContent>
    </Card>
  );
}
